import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell, Moon, Sun, Menu, LogOut, User as UserIcon, Settings, ChevronDown,
  CheckCheck, ArrowRight, Clock, CircleHelp,
  AlertTriangle, AlertCircle, CheckCircle2, RotateCcw, Archive, ShieldAlert, MessageSquare, Send, Wine,
} from 'lucide-react';
import { GlobalSearch } from '@/shared/components/layout/GlobalSearch';
import { helpTopicForPath, helpHrefForPath } from '@/features/help/contextHelp';
import { api } from '@/shared/lib/api';
import { useUIStore } from '@store/ui.store';
import { useAuthStore } from '@store/auth.store';
import { useLogout } from '@/shared/hooks/useAuth';
import { getInitials, ROLE_CONFIG, cn } from '@/shared/lib/utils';
import { mergePreferences } from '@/shared/lib/theme';

interface TopbarProps { actions?: React.ReactNode }

interface Notif { id: string; type: string; title: string; body: string; isRead: boolean; createdAt: string; entityType?: string | null }

const TYPE_CFG: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  FORM_SUBMITTED:   { icon: Send,         color: '#2563eb', bg: '#dbeafe' },
  FORM_APPROVED:    { icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' },
  FORM_RETURNED:    { icon: RotateCcw,    color: '#b45309', bg: '#fef3c7' },
  FORM_CLOSED:      { icon: Archive,      color: '#64748b', bg: '#f1f5f9' },
  INCIDENT_CREATED: { icon: AlertTriangle,color: '#dc2626', bg: '#fee2e2' },
  INCIDENT_UPDATED: { icon: AlertCircle,  color: '#ea580c', bg: '#ffedd5' },
  SYSTEM_ALERT:     { icon: ShieldAlert,  color: '#7c3aed', bg: '#ede9fe' },
  MESSAGE:          { icon: MessageSquare,color: '#6366f1', bg: '#e0e7ff' },
};
/* B-653: alarm kontroli trzeźwości przychodzi jako `SYSTEM_ALERT` — w dzwonku rozpoznajemy go po `entityType`
   i malujemy na czerwono z ikoną trzeźwości (fiolet `SYSTEM_ALERT` ginąłby wśród zwykłych komunikatów). */
const CFG_TRZEZWOSCI = { icon: Wine, color: '#e11d48', bg: '#ffe4e6' };
const cfgFor = (t: string, entityType?: string | null) =>
  entityType === 'SobrietyAlert' ? CFG_TRZEZWOSCI : TYPE_CFG[t] ?? { icon: Bell, color: '#0d9488', bg: '#ccfbf1' };

function relativeTime(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'przed chwilą';
  const m = Math.floor(s / 60); if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} godz temu`;
  const d = Math.floor(h / 24); if (d === 1) return 'wczoraj'; if (d < 7) return `${d} dni temu`;
  return new Date(iso).toLocaleDateString('pl-PL');
}

export function Topbar({ actions }: TopbarProps) {
  const { darkMode, toggleSidebar, setTheme } = useUIStore();
  const { user, updateUser } = useAuthStore();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  // Live clock (updates every 30 s)
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(t); }, []);
  const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' });

  // Live ops-pulse — forms awaiting approval (dispatchers/admins only)
  const isDispatcher = user?.role === 'ADMIN' || user?.role === 'DISPATCHER';
  const pendingQ = useQuery({
    queryKey: ['topbar', 'pending-approvals'],
    queryFn: async () => { const r: any = await api.get('/forms/submissions', { status: 'SUBMITTED', limit: 1 }); return Number(r?.meta?.total ?? 0); },
    enabled: isDispatcher,
    refetchInterval: 60_000,
  });
  const pending = pendingQ.data ?? 0;

  // Notifications — recent list + unread count
  const notifQ = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: async () => (await api.get<any>('/notifications')) as Notif[],
    refetchInterval: 30_000,
  });
  const items: Notif[] = (Array.isArray(notifQ.data) ? notifQ.data : ((notifQ.data as any)?.data ?? [])).slice(0, 8);
  const countQ = useQuery({
    queryKey: ['sidebar', 'unread-notifications'],
    queryFn: async () => { const r: any = await api.get('/notifications/count'); return Number(r?.count ?? r?.data?.count ?? 0); },
    refetchInterval: 30_000,
  });
  const unreadCount = countQ.data ?? 0;

  const invalidateNotifs = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['sidebar', 'unread-notifications'] });
  };
  const markRead = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`, {}), onSuccess: invalidateNotifs });
  const markAllRead = useMutation({ mutationFn: () => api.post('/notifications/read-all', {}), onSuccess: invalidateNotifs });

  // Close dropdowns on outside click / Esc
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setUserMenuOpen(false); setBellOpen(false); } };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, []);

  const fullName = user ? `${user.firstName} ${user.lastName}` : '—';
  const roleLabel = user ? (ROLE_CONFIG[user.role]?.label ?? user.role) : '';

  const handleRow = (n: Notif) => {
    if (!n.isRead) markRead.mutate(n.id);
    setBellOpen(false);
    navigate('/notifications');
  };

  return (
    <header
      className="relative sticky top-0 z-[3000] flex h-16 items-center gap-4 px-4 border-b border-white/10
                 text-white bg-gradient-to-b from-[#0e1d33] to-[#0a1626] backdrop-blur"
    >
      {/* Animated brand aurora accent along the bottom edge (blue → cyan → green) */}
      <style>{`@keyframes zur-aurora { from { background-position: 0% 50%; } to { background-position: 200% 50%; } }`}</style>
      <span aria-hidden className="absolute left-0 right-0 bottom-0 h-[3px]"
        style={{ background: 'linear-gradient(90deg,#2b8fe0,#2f9fb6,#5fbb35,#06b6d4,#2b8fe0)', backgroundSize: '200% auto', animation: 'zur-aurora 9s linear infinite', opacity: 0.85 }} />

      {/* Mobile menu toggle */}
      <button onClick={toggleSidebar} className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors md:hidden">
        <Menu className="h-5 w-5" />
      </button>

      {/* Live clock */}
      <style>{`@keyframes zur-live-dot { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,.55); } 70% { box-shadow: 0 0 0 6px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }`}</style>
      <div className="hidden md:flex items-center gap-2.5 shrink-0">
        <span className="flex items-center justify-center rounded-xl shrink-0 h-9 w-9 text-sky-300 ring-1 ring-white/10"
          style={{ background: 'linear-gradient(135deg,rgba(43,143,224,0.28),rgba(95,187,53,0.22))' }}>
          <Clock className="w-[18px] h-[18px]" />
        </span>
        <div className="leading-tight">
          <p className="text-[15px] font-bold text-white tabular-nums tracking-tight">{timeStr}</p>
          <p className="text-[11px] font-semibold text-sky-300/80 capitalize">{dateStr}</p>
        </div>
      </div>

      {/* Ops-pulse — forms awaiting approval */}
      {isDispatcher && (
        <button
          onClick={() => navigate('/forms/my?view=board')}
          title={pending > 0 ? `${pending} ${pending === 1 ? 'formularz oczekuje' : 'formularzy oczekuje'} na zatwierdzenie` : 'Brak formularzy do zatwierdzenia'}
          className={cn('hidden lg:flex items-center gap-1.5 shrink-0 rounded-full border pl-1.5 pr-2.5 py-1 transition-colors',
            pending > 0
              ? 'border-amber-400/40 bg-amber-500/15 hover:bg-amber-500/25'
              : 'border-white/15 bg-white/5 hover:bg-white/10')}>
          <span aria-hidden className="rounded-full shrink-0"
            style={{ width: 8, height: 8, background: pending > 0 ? '#f59e0b' : '#94a3b8', animation: pending > 0 ? 'zur-live-dot 2s ease-out infinite' : 'none' }} />
          <span className={cn('text-[13px] font-extrabold tabular-nums', pending > 0 ? 'text-amber-300' : 'text-white/80')}>{pending}</span>
          <span className="text-[11px] font-medium text-white/60">do zatwierdzenia</span>
        </button>
      )}

      <span className="hidden md:block w-px h-8 shrink-0 bg-white/10" />

      {/* Global search */}
      <div className="flex-1 flex justify-center px-2">
        <GlobalSearch />
      </div>

      {actions && <div className="flex items-center gap-2">{actions}</div>}

      {/* Controls */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Help kontekstowy — „?" prowadzi do sekcji Pomocy opisującej BIEŻĄCY
            ekran (mapa tras w features/help/contextHelp.ts); bez dopasowania
            otwiera samą Pomoc. Ukryty na /pomoc — tam by wskazywał sam siebie. */}
        {location.pathname !== '/pomoc' && (
          <button onClick={() => navigate(helpHrefForPath(location.pathname))}
            title={helpTopicForPath(location.pathname) ? `Pomoc: ${helpTopicForPath(location.pathname)}` : 'Centrum Pomocy'}
            className="rounded-xl p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors">
            <CircleHelp className="h-4 w-4" />
          </button>
        )}
        {/* Dark mode */}
        <button
          onClick={() => {
            const theme: 'light' | 'dark' = darkMode ? 'light' : 'dark';
            const next = { ...mergePreferences(user?.uiPreferences), theme };
            setTheme(theme);
            updateUser({ uiPreferences: next });
            api.patch('/auth/preferences', next).catch(() => {});
          }}
          title="Tryb ciemny"
          className="rounded-xl p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors">
          {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Bell + dropdown */}
        <div className="relative" ref={bellRef}>
          <button onClick={() => setBellOpen((v) => !v)}
            aria-label={`Powiadomienia${unreadCount > 0 ? ` (${unreadCount} nieprzeczytane)` : ''}`}
            className={cn('relative rounded-xl p-2 transition-colors',
              bellOpen ? 'bg-emerald-500/20 text-emerald-300' : 'text-white/70 hover:bg-white/10 hover:text-white')}>
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <>
                <span aria-hidden className="absolute rounded-full pointer-events-none" style={{ top: -2, right: -2, width: 18, height: 18, animation: 'zur-bell-pulse 2.2s ease-out infinite' }} />
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full ring-2 ring-[#0a1626] flex items-center justify-center text-[10px] font-bold text-white bg-red-500">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
                <style>{`@keyframes zur-bell-pulse { 0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.45);} 70% { box-shadow: 0 0 0 9px rgba(239,68,68,0);} 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0);} }`}</style>
              </>
            )}
          </button>

          {bellOpen && (
            <div className="absolute right-0 top-full mt-2 w-[400px] max-w-[92vw] rounded-2xl overflow-hidden z-50 bg-popover border border-border shadow-2xl">
              {/* Hero */}
              <div className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg,#065F46 0%,#047857 55%,#10B981 100%)' }}>
                <div className="absolute -top-14 -right-10 w-40 h-40 rounded-full" style={{ background: 'rgba(255,255,255,0.10)' }} />
                <div className="relative px-4 py-3.5 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.20)', border: '1px solid rgba(255,255,255,0.25)' }}>
                      <Bell className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold tracking-[0.18em] opacity-85">SKRZYNKA</p>
                      <p className="text-lg font-extrabold leading-none">Powiadomienia</p>
                      <p className="text-[11px] mt-0.5 opacity-85">{unreadCount === 0 ? 'Wszystko przeczytane' : `${unreadCount} nieprzeczytanych`}</p>
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <button onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-extrabold disabled:opacity-50 shrink-0"
                      style={{ background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.30)' }}>
                      <CheckCheck className="w-3 h-3" /> Oznacz wszystkie
                    </button>
                  )}
                </div>
              </div>

              {/* List */}
              <div className="max-h-[420px] overflow-y-auto">
                {notifQ.isLoading && items.length === 0 && (
                  <div className="px-4 py-10 text-center text-xs text-muted-foreground">Ładowanie…</div>
                )}
                {!notifQ.isLoading && items.length === 0 && (
                  <div className="px-4 py-12 text-center">
                    <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)' }}>
                      <CheckCheck className="w-6 h-6" style={{ color: '#047857' }} />
                    </div>
                    <p className="text-sm font-extrabold text-foreground">Brak powiadomień</p>
                    <p className="text-[11px] text-muted-foreground mt-1 max-w-[260px] mx-auto">Zdarzenia, statusy formularzy i alerty pojawią się tutaj.</p>
                  </div>
                )}
                {items.map((n) => {
                  const cfg = cfgFor(n.type, n.entityType); const Icon = cfg.icon; const unread = !n.isRead;
                  return (
                    <button key={n.id} onClick={() => handleRow(n)}
                      className={cn('group w-full px-4 py-3 flex items-start gap-3 text-left transition-colors border-b border-border/60 last:border-0 relative',
                        unread ? 'bg-amber-50/60 dark:bg-amber-500/5 hover:bg-amber-100/60' : 'hover:bg-muted/50')}>
                      {unread && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full" style={{ background: cfg.color }} />}
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}22` }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn('text-[12.5px] leading-snug line-clamp-2', unread ? 'font-extrabold text-foreground' : 'font-semibold text-foreground/80')}>{n.title}</p>
                          <span className="text-[10px] font-bold text-muted-foreground shrink-0 mt-0.5 tabular-nums">{relativeTime(n.createdAt)}</span>
                        </div>
                        <p className="text-[11.5px] text-muted-foreground leading-snug mt-1 line-clamp-2">{n.body}</p>
                      </div>
                      {unread && <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: cfg.color }} />}
                    </button>
                  );
                })}
              </div>

              {/* Footer CTA */}
              <div className="px-3 py-2.5 border-t border-border bg-muted/30">
                <button onClick={() => { setBellOpen(false); navigate('/notifications'); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-extrabold text-white"
                  style={{ background: 'linear-gradient(135deg,#047857,#10B981)', boxShadow: '0 2px 8px rgba(16,185,129,0.30)' }}>
                  Zobacz wszystkie powiadomienia <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <span className="w-px h-7 bg-white/10" />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button onClick={() => setUserMenuOpen((o) => !o)}
            className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-white/10 transition-colors">
            <div className="flex h-8 w-8 items-center justify-center rounded-full text-white text-xs font-bold shrink-0 ring-1 ring-white/15"
              style={{ background: 'linear-gradient(135deg,#2b8fe0,#5fbb35)' }}>
              {user ? getInitials(fullName) : '?'}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-semibold leading-none text-white">{fullName}</p>
              <p className="text-xs text-white/60 mt-0.5">{roleLabel}</p>
            </div>
            <ChevronDown className={cn('h-4 w-4 text-white/60 hidden md:block transition-transform', userMenuOpen && 'rotate-180')} />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 z-50 w-56 rounded-xl border border-border bg-popover shadow-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold text-foreground">{fullName}</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{user?.email}</p>
                <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">{roleLabel}</span>
              </div>
              <div className="py-1">
                <button onClick={() => { setUserMenuOpen(false); navigate('/profile'); }} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-muted transition-colors text-left text-foreground">
                  <UserIcon className="h-4 w-4 text-muted-foreground" /> Mój profil
                </button>
                <button onClick={() => { setUserMenuOpen(false); navigate('/change-password'); }} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-muted transition-colors text-left text-foreground">
                  <Settings className="h-4 w-4 text-muted-foreground" /> Zmień hasło
                </button>
                <div className="my-1 border-t border-border" />
                <button onClick={() => { setUserMenuOpen(false); logout(); }} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors text-left">
                  <LogOut className="h-4 w-4" /> Wyloguj się
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
